import React, { useState } from 'react';
import { Check, Zap, Crown, Rocket, CreditCard, ArrowRight } from 'lucide-react';
import './Plans.css';
import './AnimatedButtons.css';

function Plans() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentPlan] = useState('free');

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      icon: Zap,
      price: { monthly: 0, yearly: 0 },
      description: 'Para começar a criar conteúdo',
      features: [
        '500 créditos de energia/mês',
        'Até 10 posts por mês',
        '2 vídeos por mês',
        'Templates básicos',
        'Suporte por email'
      ],
      limitations: [
        'Sem API de integração',
        'Sem agendamento',
        'Marca d\'água nos vídeos'
      ],
      cta: 'Plano Atual',
      popular: false
    },
    {
      id: 'pro',
      name: 'Profissional',
      icon: Crown,
      price: { monthly: 97, yearly: 970 },
      description: 'Para criadores e pequenas empresas',
      features: [
        '5.000 créditos de energia/mês',
        'Posts ilimitados',
        '50 vídeos por mês',
        'Todos os templates',
        'Agendamento de posts',
        'Análise de performance',
        'Integração com redes sociais',
        'Suporte prioritário',
        'Sem marca d\'água'
      ],
      limitations: [],
      cta: 'Assinar Agora',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Empresarial',
      icon: Rocket,
      price: { monthly: 297, yearly: 2970 },
      description: 'Para agências e grandes empresas',
      features: [
        'Créditos ilimitados',
        'Tudo ilimitado',
        'Vídeos ilimitados',
        'Templates personalizados',
        'API completa',
        'Múltiplas marcas',
        '10+ usuários',
        'Relatórios avançados',
        'Suporte dedicado 24/7',
        'Treinamento personalizado',
        'White label'
      ],
      limitations: [],
      cta: 'Falar com Vendas',
      popular: false
    }
  ];

  const handleUpgrade = (planId) => {
    if (planId === currentPlan) return;
    
    if (planId === 'enterprise') {
      alert('Em breve você será redirecionado para falar com nossa equipe de vendas!');
      return;
    }
    
    alert(`Você será redirecionado para o pagamento do plano ${plans.find(p => p.id === planId).name}!`);
  };

  return (
    <div className="plans-container">
      <div className="billing-toggle">
        <button 
          className={billingCycle === 'monthly' ? 'active' : ''}
          onClick={() => setBillingCycle('monthly')}
        >
          Mensal
        </button>
        <button 
          className={billingCycle === 'yearly' ? 'active' : ''}
          onClick={() => setBillingCycle('yearly')}
        >
          Anual
          <span className="discount-badge">Economize 17%</span>
        </button>
      </div>

      <div className="plans-grid">
        {plans.map(plan => {
          const IconComponent = plan.icon;
          const price = plan.price[billingCycle];
          const isCurrentPlan = plan.id === currentPlan;
          
          return (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Mais Popular</div>}
              {isCurrentPlan && <div className="current-badge">Plano Atual</div>}
              
              <div className="plan-icon">
                <IconComponent size={32} />
              </div>
              
              <h2>{plan.name}</h2>
              <p className="plan-description">{plan.description}</p>
              
              <div className="plan-price">
                <span className="currency">R$</span>
                <span className="amount">{price}</span>
                <span className="period">/{billingCycle === 'monthly' ? 'mês' : 'ano'}</span>
              </div>
              
              {billingCycle === 'yearly' && price > 0 && (
                <p className="yearly-note">
                  ou 12x de R$ {(price / 12).toFixed(2)}
                </p>
              )}
              
              <ul className="features-list">
                {plan.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <Check size={18} className="check-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
                {plan.limitations.map((limitation, index) => (
                  <li key={`limit-${index}`} className="feature-item disabled">
                    <span className="x-icon">×</span>
                    <span>{limitation}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                className={`btn-animated btn-animated-full ${isCurrentPlan ? '' : plan.popular ? 'btn-animated-gold' : ''}`}
                onClick={() => handleUpgrade(plan.id)}
                disabled={isCurrentPlan}
              >
                <span>
                  {isCurrentPlan ? plan.cta : (
                    <>
                      {plan.cta}
                      <ArrowRight size={18} />
                    </>
                  )}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      <section className="plans-faq">
        <h2>Perguntas Frequentes</h2>
        
        <div className="faq-item">
          <h3>O que são créditos de energia?</h3>
          <p>Créditos de energia são usados para gerar conteúdo. Cada post consome 10 créditos, vídeos consomem de 50 a 160 créditos dependendo da duração.</p>
        </div>
        
        <div className="faq-item">
          <h3>Posso cancelar a qualquer momento?</h3>
          <p>Sim! Não há fidelidade. Você pode cancelar seu plano a qualquer momento e continuar usando até o final do período pago.</p>
        </div>
        
        <div className="faq-item">
          <h3>Como funciona o pagamento anual?</h3>
          <p>No plano anual, você paga uma vez por ano e economiza 17%. Você pode parcelar em até 12x no cartão de crédito.</p>
        </div>
        
        <div className="faq-item">
          <h3>Posso fazer upgrade depois?</h3>
          <p>Sim! Você pode fazer upgrade ou downgrade a qualquer momento. Faremos o ajuste proporcional do valor.</p>
        </div>
      </section>

      <section className="payment-methods">
        <h3>Métodos de Pagamento Aceitos</h3>
        <div className="payment-icons">
          <CreditCard size={32} />
          <span>Cartão de Crédito</span>
          <span>•</span>
          <span>Pix</span>
          <span>•</span>
          <span>Boleto</span>
        </div>
        <p className="payment-note">Pagamento 100% seguro e criptografado</p>
      </section>
    </div>
  );
}

export default Plans;
